/**
 * Enhanced smooth scroll — ports v1's scroll intelligence to v2.
 *
 * Features:
 * - Custom `requestAnimationFrame` animation with easeInOutCubic easing
 * - `block: "start" | "center" | "end" | "nearest"` positioning
 * - Respects CSS `scroll-margin-top/bottom` on target element
 * - Returns cancel function for aborting mid-scroll
 * - **Keyboard-aware**: automatically accounts for keyboard overlay when
 *   `KeyboardResize.None` is active (reads from keyboardStore).
 *
 * v1 ref: src/utils/ui/enhancedScrollIntoView.ts (363 lines)
 */

import { getKeyboardOverlayHeight } from "@/features/auth/stores/keyboardStore"

export interface SmoothScrollOptions {
  /** Scroll container element. Defaults to the nearest scrollable ancestor of the target. */
  container?: HTMLElement | null
  /** Animation duration in ms. Default: 800 */
  duration?: number
  /** Vertical alignment. Default: "start" */
  block?: "start" | "center" | "end" | "nearest"
  /** Extra pixel offset from target position. Default: 0 */
  offset?: number
  /** Pixels at the bottom of the container obscured by fixed UI (toolbar, action panel). Default: 0 */
  bottomInset?: number
  /** Callback when scroll animation completes */
  onComplete?: () => void
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
}

/**
 * Smooth-scroll `target` into view within its scroll container.
 *
 * Automatically detects keyboard overlay (KeyboardResize.None) and
 * adds it to the effective bottom inset so elements behind the keyboard
 * are correctly scrolled into the visible area.
 *
 * @returns A function that cancels the animation.
 */
export function enhancedSmoothScroll(
  target: HTMLElement,
  options: SmoothScrollOptions = {},
): () => void {
  const { duration = 800, block = "start", offset = 0, bottomInset = 0, onComplete } = options

  // Find scroll container: explicit option → closest scrollable ancestor → body
  const container =
    options.container ??
    target.closest<HTMLElement>("main.overflow-y-auto") ??
    target.closest<HTMLElement>(".overflow-y-auto") ??
    document.querySelector<HTMLElement>("main.overflow-y-auto") ??
    document.body

  if (!target || !container) return () => {}

  // Keyboard overlay in "none" resize mode (keyboard overlays from bottom)
  const keyboardOverlay = getKeyboardOverlayHeight()
  const effectiveBottomInset = bottomInset + keyboardOverlay

  // ── Calculate target scroll position ────────────────────────────────
  const targetRect = target.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  // Respect CSS scroll-margin-top on target
  const computedStyle = window.getComputedStyle(target)
  const scrollMarginTop = parseFloat(computedStyle.scrollMarginTop) || 0
  const scrollMarginBottom = parseFloat(computedStyle.scrollMarginBottom) || 0

  // Relative position of target within scroll container
  const relativeTop = targetRect.top - containerRect.top + container.scrollTop

  // Visible height (accounting for fixed UI + keyboard overlay)
  const visibleHeight = container.clientHeight - effectiveBottomInset

  let targetScrollTop: number

  switch (block) {
    case "start":
      targetScrollTop = relativeTop - scrollMarginTop + offset
      break
    case "center":
      targetScrollTop =
        relativeTop -
        visibleHeight / 2 +
        targetRect.height / 2 +
        offset
      break
    case "end":
      targetScrollTop =
        relativeTop -
        visibleHeight +
        targetRect.height +
        scrollMarginBottom +
        offset
      break
    case "nearest": {
      const elementTop = targetRect.top - containerRect.top
      const elementBottom = elementTop + targetRect.height
      const isAbove = elementTop < scrollMarginTop
      const isBelow = elementBottom > visibleHeight - scrollMarginBottom
      if (!isAbove && !isBelow) {
        // Already in view
        onComplete?.()
        return () => {}
      }
      targetScrollTop = isAbove
        ? relativeTop - scrollMarginTop + offset
        : relativeTop -
          visibleHeight +
          targetRect.height +
          scrollMarginBottom +
          offset
      break
    }
  }

  // Clamp
  const maxScroll = container.scrollHeight - container.clientHeight
  targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll))

  // ── Animate ─────────────────────────────────────────────────────────
  const startPos = container.scrollTop
  const distance = targetScrollTop - startPos

  // Skip animation if already close enough
  if (Math.abs(distance) < 2) {
    onComplete?.()
    return () => {}
  }

  let startTime: number | null = null
  let rafId: number
  let cancelled = false

  // ── Cancel on manual scroll ───────────────────────────────────────
  // Listen for user-initiated scroll gestures (wheel, touch, pointer)
  // and abort the programmatic animation when detected.
  const cancelEvents = ["wheel", "touchstart", "pointerdown"] as const

  function onManualScroll() {
    if (!cancelled) {
      cancelled = true
      cancelAnimationFrame(rafId)
      cleanup()
    }
  }

  function cleanup() {
    for (const evt of cancelEvents) {
      container.removeEventListener(evt, onManualScroll)
    }
  }

  for (const evt of cancelEvents) {
    container.addEventListener(evt, onManualScroll, { passive: true })
  }

  function step(timestamp: number) {
    if (cancelled) return

    if (startTime === null) startTime = timestamp
    const elapsed = timestamp - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = easeInOutCubic(progress)

    container.scrollTop = startPos + distance * eased

    if (progress < 1) {
      rafId = requestAnimationFrame(step)
    } else {
      container.scrollTop = targetScrollTop
      cleanup()
      onComplete?.()
    }
  }

  rafId = requestAnimationFrame(step)

  return () => {
    cancelled = true
    cancelAnimationFrame(rafId)
    cleanup()
  }
}

// ---------------------------------------------------------------------------
// Visibility check — determines if an element is "properly in view"
// Used by TaskCard to decide whether to scroll on open/close.
//
// v1 logic:
//   isProperlyInView = visibleHeight/elementHeight > 0.8
//                   && distanceFromCenter < viewportHeight * 0.2
// ---------------------------------------------------------------------------

/**
 * Check whether an element is "properly in view" within a scroll container.
 *
 * "Properly in view" means:
 * 1. At least 80% of the element is visible
 * 2. The element's center is within 20% of the viewport center
 *
 * Automatically accounts for keyboard overlay in KeyboardResize.None mode.
 *
 * @returns true if no scroll is needed
 */
export function isProperlyInView(
  element: HTMLElement,
  container: HTMLElement,
): boolean {
  const elRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const keyboardOverlay = getKeyboardOverlayHeight()

  // How much of the element is visible within the container
  const visibleTop = Math.max(elRect.top, containerRect.top)
  const visibleBottom = Math.min(elRect.bottom, containerRect.bottom - keyboardOverlay)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const elementHeight = elRect.height

  if (elementHeight === 0) return true

  // Element center relative to container viewport
  const containerHeight = container.clientHeight - keyboardOverlay
  const containerCenter = containerHeight / 2
  const elementCenter = elRect.top - containerRect.top + elementHeight / 2
  const distanceFromCenter = Math.abs(elementCenter - containerCenter)

  return (
    visibleHeight / elementHeight > 0.8 &&
    distanceFromCenter < containerHeight * 0.2
  )
}
